import { Download } from "lucide-react";
import { useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { isApiError, type ProjectSummary, useAdminArchiveProject } from "@/lib/api";
import { logger } from "@/lib/logger";
import { safeHttpUrl } from "@/lib/utils";

/**
 * Where an archived project's JSON dump is downloaded from.
 *
 * Served as a plain link rather than through the SDK: the admin session cookie
 * authenticates it and the browser handles the attachment, so there is no blob
 * plumbing to write.
 */
export function archiveDownloadUrl(projectId: number): string {
  return `/api/admin/projects/${projectId}/archive/download`;
}

const STORAGE_CONFLICT = "storage_objects_present";
const REPO_MISSING_CONFLICT = "repo_missing";
const RAILWAY_UNKNOWN_CONFLICT = "railway_state_unknown";

/**
 * The `detail.error` codes a forced retry can actually override. Membership
 * rather than equality, so an unknown code defaults to not forceable, which is
 * the safe way to be wrong.
 */
const FORCEABLE_CONFLICTS = new Set<string>([
  STORAGE_CONFLICT,
  REPO_MISSING_CONFLICT,
  RAILWAY_UNKNOWN_CONFLICT,
]);

/** The archive endpoint's response, inferred so no API type is hand-written. */
type ArchiveResult = NonNullable<ReturnType<typeof useAdminArchiveProject>["data"]>;

function pluralize(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? "" : "s"}`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * A 409 refusal from the archive endpoint.
 *
 * `title` and `confirmLabel` exist only on the forceable variant, so the render
 * cannot reach for a label that has no destructive button to sit on. Each
 * forceable case names what the force actually costs: losing Storage files and
 * losing a restorable schema are different losses.
 */
type ArchiveConflict =
  | { forceable: false; message: string }
  | { forceable: true; message: string; title: string; confirmLabel: string };

/**
 * Read a 409 body.
 *
 * The endpoint refuses with a 409 for several reasons and only some of them can
 * be overridden with `force`. "Already archived" and "still has a prod
 * environment" answer a forced retry with the identical 409, so offering a retry
 * button for them would be a dead end.
 *
 * Only the forceable ones carry a structured `detail` tagged with a code. The
 * rest send a plain string, where reading `.error` is simply undefined, so they
 * fall through to the render-verbatim branch. Matching the code rather than the
 * body shape keeps that true if a non-forceable refusal ever grows a structured
 * detail of its own.
 */
function readConflict(body: unknown): ArchiveConflict {
  const detail = (body as { detail?: unknown } | null)?.detail;
  // Reading `.error` off a plain string is simply undefined, so the
  // non-forceable refusals fall straight through to the branch below.
  const structured = detail as
    { error?: unknown; message?: unknown; storage_object_count?: unknown } | null | undefined;
  const code = typeof structured?.error === "string" ? structured.error : null;
  const message = typeof structured?.message === "string" ? structured.message.trim() : "";

  if (code !== null && FORCEABLE_CONFLICTS.has(code)) {
    if (code === STORAGE_CONFLICT) {
      const fileCount = structured?.storage_object_count;
      return {
        forceable: true,
        title: "Storage files will be lost",
        confirmLabel: "Archive and lose the files",
        // Composed from the count rather than scraped out of the backend's
        // prose, which is free to be reworded. The prose is the fallback.
        message:
          (typeof fileCount === "number"
            ? `This project holds ${pluralize(fileCount, "Supabase Storage file")}, which cannot be included in the saved data.`
            : message ||
              "This project holds Supabase Storage files, which cannot be included in the saved data.") +
          " Everything else (database rows and auth users) is still saved, but the Storage files go with the Supabase project and cannot be recovered.",
      };
    }
    if (code === RAILWAY_UNKNOWN_CONFLICT) {
      return {
        forceable: true,
        title: "Railway could not be verified",
        confirmLabel: "Archive anyway",
        message:
          message ||
          "This project was set up with Railway, but nothing here can name its Railway project, so there is no way to check whether one is still running. Open Railway and delete it by hand if it is there, then archive again.",
      };
    }
    // `repo_missing` carries no number to compose from, so its prose is read
    // out verbatim.
    return {
      forceable: true,
      title: "This archive cannot be restored",
      confirmLabel: "Archive anyway",
      message:
        message ||
        "This project's GitHub repository is gone, so the saved data cannot be rebuilt from it later.",
    };
  }

  if (typeof detail === "string" && detail.trim()) {
    return { forceable: false, message: detail };
  }

  // An unrecognised code, or a body with no readable text at all. The message
  // is always non-empty: it is rendered as the only explanation the admin gets.
  return { forceable: false, message: message || "This project cannot be archived right now." };
}

interface ProjectArchiveDialogProps {
  project: ProjectSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProjectArchiveDialog({ project, open, onOpenChange }: ProjectArchiveDialogProps) {
  const archiveProject = useAdminArchiveProject();
  const [result, setResult] = useState<ArchiveResult | null>(null);
  const [conflict, setConflict] = useState<ArchiveConflict | null>(null);
  const [error, setError] = useState("");
  const archiving = archiveProject.isPending;

  const runArchive = async (force: boolean) => {
    if (!project) {
      return;
    }
    setError("");
    try {
      const data = await archiveProject.mutateAsync({ projectId: project.id, force });
      setConflict(null);
      setResult(data);
    } catch (err) {
      logger.error("[project-archive] failed:", err);
      // A 409 is the backend refusing on purpose, and its reason is the only
      // thing worth showing. Never retry with force automatically.
      if (isApiError(err) && err.status === 409) {
        const parsed = readConflict(err.body);
        // A forced attempt that still refuses cannot be cleared by forcing
        // again (someone else archived it, or it gained a prod environment),
        // so re-offering the destructive confirm would be a trap. Demote the
        // refusal to non-forceable, which leaves the reason on screen above a
        // plain Close.
        setConflict(force ? { forceable: false, message: parsed.message } : parsed);
        return;
      }
      setError("Failed to archive project. Please try again.");
    }
  };

  // AlertDialogAction closes the dialog on click by default. Prevent that so a
  // failure, a refusal, and the result stats all stay on screen.
  const handleArchive = (e: React.MouseEvent, force: boolean) => {
    e.preventDefault();
    void runArchive(force);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      // Don't let a stale result or error greet the next open.
      setResult(null);
      setConflict(null);
      setError("");
    }
    onOpenChange(next);
  };

  // A dump already stored on a project that was never marked shut down means
  // an earlier run stopped partway. Archiving again resumes it rather than
  // starting over, so the confirm must not threaten a deletion that happened.
  const resuming = project?.has_archive === true && project?.shut_down !== true;

  const stats = result?.stats ?? null;
  // A resumed archive reports no stats, because it skips the dump an earlier
  // run already stored. So success is read from `success`, and the dump's
  // existence from either having just stored one or resuming onto one.
  const archived = result?.success === true;
  const dumpAvailable = stats !== null || resuming;
  // Reported even when the archive succeeded overall: a resource that survived
  // teardown keeps billing, and the admin has to go remove it by hand.
  const failures = Object.entries(result?.results ?? {}).filter(([, r]) => !r.success);

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {result
              ? archived
                ? `Archived ${project?.project_name}`
                : `Could not archive ${project?.project_name}`
              : conflict
                ? conflict.forceable
                  ? conflict.title
                  : `Cannot archive ${project?.project_name}`
                : resuming
                  ? `Finish archiving ${project?.project_name}?`
                  : `Archive ${project?.project_name}?`}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {result ? (
              <>
                {stats
                  ? `Saved ${pluralize(stats.table_count, "table")}, ${pluralize(stats.row_count, "row")}` +
                    (stats.auth_user_count
                      ? `, ${pluralize(stats.auth_user_count, "auth user")}`
                      : "") +
                    ` (${formatBytes(stats.dump_bytes)}).`
                  : archived
                    ? "The archive is complete."
                    : "No dump was stored."}{" "}
                {dumpAvailable
                  ? "The dump stays downloadable from this project's row."
                  : "Check the resource outcomes below."}
              </>
            ) : conflict ? (
              conflict.message
            ) : resuming ? (
              <>
                Archiving <strong>{project?.project_name}</strong> stopped partway, after its data
                had already been saved. Finishing it
                {project?.railway_dashboard_url ? " deletes its Railway project and" : ""} marks the
                project archived. The saved data is <strong>kept</strong> and stays downloadable,
                and the GitHub repo is untouched.
              </>
            ) : (
              <>
                Archiving <strong>{project?.project_name}</strong> saves its database and auth users
                to a JSON dump you can download later, then <strong>deletes</strong> its Supabase
                project
                {project?.railway_dashboard_url ? " and its Railway project" : ""}. That deletion is
                permanent. The GitHub repo is <strong>kept</strong> and untouched, and the project
                stays listed here so its dump remains available.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {result && stats !== null && stats.storage_object_count > 0 && (
          <p className="text-muted text-sm">
            {pluralize(stats.storage_object_count, "Storage file")} could not be included in the
            dump.
          </p>
        )}

        {result?.error && <p className="text-destructive text-sm">{result.error}</p>}

        {failures.length > 0 && (
          <div className="text-sm">
            <p className="text-destructive font-medium">
              Some resources were not removed and may still be billing:
            </p>
            <ul className="text-muted mt-1 list-inside list-disc">
              {failures.map(([name, outcome]) => {
                const manualUrl = safeHttpUrl(outcome.manual_url);
                return (
                  <li key={name}>
                    <span className="capitalize">{name.replace(/_/g, " ")}</span>:{" "}
                    {outcome.error ?? "removal failed"}
                    {manualUrl && (
                      <>
                        {" "}
                        <a
                          href={manualUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline underline-offset-2"
                        >
                          remove it manually
                        </a>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {error && <p className="text-destructive text-sm">{error}</p>}

        <AlertDialogFooter>
          {conflict && !conflict.forceable ? (
            // Nothing to retry: a forced archive answers with the same refusal.
            <AlertDialogCancel>Close</AlertDialogCancel>
          ) : result ? (
            <>
              {project && dumpAvailable && (
                <Button variant="outline" asChild>
                  <a href={archiveDownloadUrl(project.id)} download>
                    <Download className="mr-2 size-4" /> Download dump
                  </a>
                </Button>
              )}
              <AlertDialogAction>Close</AlertDialogAction>
            </>
          ) : (
            <>
              <AlertDialogCancel disabled={archiving}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                onClick={(e) => handleArchive(e, conflict !== null)}
                disabled={archiving}
              >
                {archiving
                  ? "Archiving..."
                  : conflict?.forceable
                    ? conflict.confirmLabel
                    : resuming
                      ? "Finish archiving"
                      : "Archive project"}
              </AlertDialogAction>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
